import { useState } from 'react';
import { motion } from 'framer-motion';
import { createLobby } from '../lib/firebase/collections';
import { getOrCreateUserId, getStoredPlayerName, setStoredPlayerName } from '../utils/userUtils';
import type { GameSettings } from '../types/game';

interface LobbyCreationProps {
  onCreateLobby: (lobbyId: string) => void;
  categories: { id: string; name: string }[];
}

export default function LobbyCreation({ onCreateLobby, categories }: LobbyCreationProps) {
  const [hostName, setHostName] = useState(() => getStoredPlayerName());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const userId = getOrCreateUserId();

  // Kategorie-Auswahl
  const [selectedCategoryId, setSelectedCategoryId] = useState(categories.length > 0 ? categories[0].id : '');

  // Standard-Spieleinstellungen
  const [gameSettings, setGameSettings] = useState<GameSettings>({
    timerEnabled: true,
    roundTimeSeconds: 90,
    winningScore: 10,
    maxRounds: undefined,
    allowLateJoin: true,
    showPlayerNames: false,
    autoStartNextRound: true,
    handSize: 7,
    allowSkipCards: false
  });

  const handleSettingChange = (key: keyof GameSettings, value: any) => {
    setGameSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostName.trim()) {
      alert("Bitte gib einen Namen ein.");
      return;
    }
    if (!selectedCategoryId) {
      alert("Bitte wähle eine Kategorie aus.");
      return;
    }
    setStoredPlayerName(hostName);
    try {
      const lobbyId = await createLobby(
        hostName.trim(),
        userId,
        selectedCategoryId,
        gameSettings
      );
      onCreateLobby(lobbyId);
    } catch (error) {
      console.error("Fehler: ", error);
      alert("Lobby konnte nicht erstellt werden.");
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#10181f] to-[#1a202c] p-4">
      <div className="w-full max-w-2xl rounded-3xl shadow-2xl p-8 sm:p-10 bg-white/10 backdrop-blur-xl border border-white/20 flex flex-col gap-8" style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)' }}>
        <h2 className="text-3xl font-extrabold text-white text-center mb-2 drop-shadow-lg">Neue Lobby erstellen</h2>
        
        <form onSubmit={handleCreate} className="flex flex-col gap-6">
          {/* Host Name */}
          <div>
            <label htmlFor="hostName" className="block text-base font-semibold text-green-200 mb-2">
              Dein Name
            </label>
            <input
              type="text"
              id="hostName"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              className="w-full rounded-lg px-4 py-3 bg-white/20 text-green-200 placeholder:text-green-300 focus:outline-none focus:ring-2 focus:ring-green-400 font-semibold"
              required
              placeholder="Wie sollen wir dich nennen?"
            />
          </div>

          {/* Kategorie-Auswahl */}
          <div>
            <label htmlFor="category" className="block text-base font-semibold text-green-200 mb-2">
              Kategorie
            </label>
            <select
              id="category"
              value={selectedCategoryId}
              onChange={e => setSelectedCategoryId(e.target.value)}
              className="w-full rounded-lg px-4 py-3 bg-white/20 text-green-200 focus:outline-none focus:ring-2 focus:ring-green-400 font-semibold"
              required
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Basis-Einstellungen */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Spieleinstellungen</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Timer Ein/Aus */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gameSettings.timerEnabled}
                    onChange={(e) => handleSettingChange('timerEnabled', e.target.checked)}
                    className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                  />
                  <span className="text-green-200 font-semibold">Timer aktiviert</span>
                </label>
              </div>

              {/* Timer-Dauer */}
              {gameSettings.timerEnabled && (
                <div>
                  <label className="block text-sm font-semibold text-green-200 mb-2">
                    Rundenzeit (Sekunden)
                  </label>
                  <input
                    type="number"
                    min="30"
                    max="300"
                    step="10"
                    value={gameSettings.roundTimeSeconds}
                    onChange={(e) => handleSettingChange('roundTimeSeconds', parseInt(e.target.value))}
                    className="w-full rounded-lg px-3 py-2 bg-white/20 text-green-200 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              )}

              {/* Gewinnpunkte */}
              <div>
                <label className="block text-sm font-semibold text-green-200 mb-2">
                  Punkte zum Sieg
                </label>
                <input
                  type="number"
                  min="3"
                  max="50"
                  value={gameSettings.winningScore}
                  onChange={(e) => handleSettingChange('winningScore', parseInt(e.target.value))}
                  className="w-full rounded-lg px-3 py-2 bg-white/20 text-green-200 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>

              {/* Handgröße */}
              <div>
                <label className="block text-sm font-semibold text-green-200 mb-2">
                  Karten pro Spieler
                </label>
                <input
                  type="number"
                  min="5"
                  max="12"
                  value={gameSettings.handSize}
                  onChange={(e) => handleSettingChange('handSize', parseInt(e.target.value))}
                  className="w-full rounded-lg px-3 py-2 bg-white/20 text-green-200 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>
            </div>
          </div>

          {/* Erweiterte Einstellungen */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-green-300 hover:text-green-200 transition-colors font-semibold"
            >
              <span>Erweiterte Einstellungen</span>
              <motion.div
                animate={{ rotate: showAdvanced ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                ▼
              </motion.div>
            </button>

            <motion.div
              initial={false}
              animate={{ height: showAdvanced ? 'auto' : 0, opacity: showAdvanced ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 mt-4">
                <div className="grid grid-cols-1 gap-4">
                  
                  {/* Maximale Runden */}
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={gameSettings.maxRounds !== undefined}
                        onChange={(e) => handleSettingChange('maxRounds', e.target.checked ? 15 : undefined)}
                        className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-green-200 font-semibold">Spiel nach X Runden beenden</span>
                    </label>
                    {gameSettings.maxRounds !== undefined && (
                      <input
                        type="number"
                        min="5"
                        max="50"
                        value={gameSettings.maxRounds}
                        onChange={(e) => handleSettingChange('maxRounds', parseInt(e.target.value))}
                        className="w-full rounded-lg px-3 py-2 bg-white/20 text-green-200 focus:outline-none focus:ring-2 focus:ring-green-400"
                        placeholder="Anzahl Runden"
                      />
                    )}
                  </div>

                  {/* Weitere Optionen */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gameSettings.allowLateJoin}
                        onChange={(e) => handleSettingChange('allowLateJoin', e.target.checked)}
                        className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-green-200">Nachträgliches Beitreten erlauben</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gameSettings.showPlayerNames}
                        onChange={(e) => handleSettingChange('showPlayerNames', e.target.checked)}
                        className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-green-200">Spielernamen bei Einreichungen zeigen</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gameSettings.autoStartNextRound}
                        onChange={(e) => handleSettingChange('autoStartNextRound', e.target.checked)}
                        className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-green-200">Automatisch nächste Runde starten</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gameSettings.allowSkipCards}
                        onChange={(e) => handleSettingChange('allowSkipCards', e.target.checked)}
                        className="w-5 h-5 rounded bg-white/20 border-2 border-green-400 text-green-500 focus:ring-green-400"
                      />
                      <span className="text-green-200">Karten tauschen erlauben</span>
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Einstellungen-Zusammenfassung */}
          <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4">
            <h4 className="text-green-300 font-semibold mb-2">Spielübersicht:</h4>
            <div className="text-sm text-green-200 space-y-1">
              <p>• {gameSettings.timerEnabled ? `${gameSettings.roundTimeSeconds}s Timer` : 'Kein Timer'}</p>
              <p>• Sieg bei {gameSettings.winningScore} Punkten{gameSettings.maxRounds ? ` oder ${gameSettings.maxRounds} Runden` : ''}</p>
              <p>• {gameSettings.handSize} Karten pro Spieler</p>
              {gameSettings.showPlayerNames && <p>• Spielernamen werden angezeigt</p>}
              {gameSettings.allowSkipCards && <p>• Karten tauschen möglich</p>}
            </div>
          </div>

          <button
            type="submit"
            disabled={!hostName.trim()}
            className="w-full rounded-xl py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-lg shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed mt-2"
          >
            Lobby erstellen
          </button>
        </form>
      </div>
    </div>
  );
} 